import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, User, Bot, Loader } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader } from '../components/Card';
import { apiUrl } from '../utils/api';

const QUICK_QUESTIONS = [
  'What closing costs should I expect?',
  'How does the stress test work?',
  'What insurance do I need?',
  'Explain the HBP program',
];

export default function AIAssistant() {
  const { chatMessages, setChatMessages } = useApp();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSend = async (text) => {
    const question = text || input.trim();
    if (!question || isLoading) return;

    setError(null);
    const userMsg = { role: 'user', content: question, id: Date.now() };
    const updatedMessages = [...chatMessages, userMsg];
    setChatMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      const conversationHistory = updatedMessages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch(apiUrl('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationHistory }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      const aiMsg = { role: 'assistant', content: data.message, id: Date.now() + 1 };
      setChatMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Bot size={22} />}
        title="AI Assistant"
        subtitle="Chat with your personal home buying advisor — powered by Claude AI."
      />

      {chatMessages.length === 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {QUICK_QUESTIONS.map(q => (
            <button key={q} onClick={() => handleSend(q)} style={{
              padding: '8px 16px', borderRadius: 20,
              background: 'var(--primary-light)', color: 'var(--primary)',
              fontWeight: 500, fontSize: 13, border: '1px solid var(--primary)',
            }}>
              {q}
            </button>
          ))}
        </div>
      )}

      <Card style={{
        minHeight: 400, display: 'flex', flexDirection: 'column',
        padding: 0, overflow: 'hidden',
      }}>
        <div style={{ flex: 1, padding: 20, overflowY: 'auto', maxHeight: 500 }}>
          {chatMessages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <MessageSquare size={48} color="var(--primary-light)" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }} />
              <p style={{ color: 'var(--gray-400)', fontSize: 15 }}>
                Start a conversation to get help with your home buying journey!
              </p>
            </div>
          ) : (
            <>
              {chatMessages.map(msg => (
                <div key={msg.id} style={{
                  display: 'flex', gap: 12, marginBottom: 20,
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: msg.role === 'user' ? 'var(--primary)' : 'var(--dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {msg.role === 'user'
                      ? <User size={16} color="white" />
                      : <Bot size={16} color="white" />
                    }
                  </div>
                  <div style={{
                    maxWidth: '80%', padding: '12px 16px',
                    borderRadius: 'var(--radius)',
                    background: msg.role === 'user' ? 'var(--primary)' : 'var(--gray-100)',
                    color: msg.role === 'user' ? 'white' : 'var(--dark)',
                  }}>
                    <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {msg.content.split('**').map((part, i) =>
                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Bot size={16} color="white" />
                  </div>
                  <div style={{
                    padding: '12px 16px', borderRadius: 'var(--radius)',
                    background: 'var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {error && (
          <div style={{
            padding: '10px 20px', background: '#fef2f2',
            borderTop: '1px solid #fecaca', fontSize: 13, color: '#dc2626',
          }}>
            {error}
          </div>
        )}

        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--gray-200)',
          display: 'flex', gap: 12,
        }}>
          <input
            type="text" placeholder="Ask a question..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={isLoading}
            style={{
              flex: 1, padding: '10px 14px',
              border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
              fontSize: 14, opacity: isLoading ? 0.6 : 1,
            }}
          />
          <button onClick={() => handleSend()} disabled={isLoading || !input.trim()} style={{
            width: 42, height: 42, borderRadius: 'var(--radius-sm)',
            background: input.trim() && !isLoading ? 'var(--primary)' : 'var(--gray-200)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Send size={18} color={input.trim() && !isLoading ? 'white' : 'var(--gray-400)'} />
          </button>
        </div>
      </Card>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
